const ISSUE_CONTEXT_FIELD_NAMES = ["declarer_id", "building_id", "room_id", "ppr_equipment_id"];
const ISSUE_FILTER_FIELD_NAMES = ["service_id", "work_category_id"];
const ISSUE_REFETCH_FIELD_NAMES = ["service_id"];

const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object || {}, key);

const hasValue = value => {
  if (Array.isArray(value)) return value.length > 0;

  return value !== undefined && value !== null && value !== "";
};

const serializeIssueFieldValue = value => {
  if (Array.isArray(value)) {
    return value.map(item => {
      if (item && typeof item === "object") {
        if (hasOwn(item, "value")) return item.value;
        if (hasOwn(item, "id")) return item.id;
      }

      return item;
    });
  }

  if (value && typeof value === "object") {
    if (hasOwn(value, "value")) return value.value;
    if (hasOwn(value, "id")) return value.id;
  }

  return value;
};

const formComponents = formData => formData?.components || formData || {};

const formFields = formData => {
  const fields = formComponents(formData)?.fields;

  return Array.isArray(fields) ? fields : [];
};

const formScheme = formData => {
  const scheme = formComponents(formData)?.scheme;

  return Array.isArray(scheme) ? scheme : [];
};

const fieldValue = (formData, values, name) => {
  if (hasOwn(values, name)) return serializeIssueFieldValue(values[name]);

  const field = formFields(formData).find(item => item?.name === name);
  return serializeIssueFieldValue(field?.value);
};

const firstValue = value => {
  const serializedValue = serializeIssueFieldValue(value);

  return Array.isArray(serializedValue) ? serializedValue[0] : serializedValue;
};

const compactParams = params =>
  Object.entries(params).reduce((result, [name, value]) => {
    if (hasValue(value)) result[name] = value;

    return result;
  }, {});

const getIssueContextParams = (formData, values = {}) => {
  const context = formData?.context || formComponents(formData)?.context || {};
  const serviceOptionsParams = formFields(formData).find(field => field?.name === "service_id")?.options_params || {};
  const workCategoryOptionsParams =
    formFields(formData).find(field => field?.name === "work_category_id")?.options_params || {};
  const equipmentId =
    context.ppr_equipment_id ??
    serviceOptionsParams.ppr_equipment_id ??
    workCategoryOptionsParams.ppr_equipment_id ??
    firstValue(values.ppr_equipment_id) ??
    firstValue(fieldValue(formData, values, "ppr_equipment_id"));

  return compactParams({
    facility_id:
      context.facility_id ??
      serviceOptionsParams.facility_id ??
      workCategoryOptionsParams.facility_id ??
      fieldValue(formData, values, "facility_id"),
    building_id:
      context.building_id ??
      serviceOptionsParams.building_id ??
      workCategoryOptionsParams.building_id ??
      fieldValue(formData, values, "building_id"),
    room_id:
      context.room_id ??
      serviceOptionsParams.room_id ??
      workCategoryOptionsParams.room_id ??
      fieldValue(formData, values, "room_id"),
    ppr_equipment_id: equipmentId,
  });
};

const normalizeFormData = formData => {
  const components = formComponents(formData);

  return {
    ...formData,
    title: formData?.title || formData?.form_header?.title?.add || "",
    root: formData?.root || "issue",
    components: {
      ...components,
      fields: formFields(formData),
      scheme: formScheme(formData),
      buttons: Array.isArray(components?.buttons) ? components.buttons : [],
    },
  };
};

const fieldDependsOn = (field, parentName) => {
  if (!field || !parentName) return false;
  if (field.depend_from?.field_name === parentName) return true;
  if (field.watch?.parent === parentName) return true;

  return Boolean(field.watch?.parents?.some(parent => parent?.parent === parentName));
};

const valuesFromIssueFormSchema = formData =>
  formFields(formData).reduce((result, field) => {
    result[field.name] = serializeIssueFieldValue(field.value);

    return result;
  }, {});

const reconcileIssueFormValues = ({
  previousValues = {},
  formData,
  changedFieldName = null,
  changedFieldValue,
  lockedValues = {},
}) =>
  formFields(formData).reduce((result, field) => {
    const name = field.name;

    if (hasOwn(lockedValues, name)) {
      result[name] = lockedValues[name];
    } else if (name === changedFieldName) {
      result[name] = serializeIssueFieldValue(changedFieldValue);
    } else if (fieldDependsOn(field, changedFieldName)) {
      result[name] = serializeIssueFieldValue(field.value);
    } else if (hasOwn(previousValues, name)) {
      result[name] = previousValues[name];
    } else {
      result[name] = serializeIssueFieldValue(field.value);
    }

    return result;
  }, {});

const getLockedIssueContext = formData => {
  const names = new Set(ISSUE_CONTEXT_FIELD_NAMES);
  const fields = formFields(formData).filter(
    field => names.has(field?.name) && Boolean(field.disabled || field.readonly),
  );
  const fieldNames = new Set(fields.map(field => field.name));
  const scheme = formScheme(formData)
    .map(row => row.filter(item => fieldNames.has(item?.name)))
    .filter(row => row.length > 0);
  const values = fields.reduce((result, field) => {
    result[field.name] = serializeIssueFieldValue(field.value);

    return result;
  }, {});

  return { fields, scheme, values, context: formData?.context || {} };
};

const mergeLockedIssueContext = (formData, lockedContext) => {
  if (!lockedContext?.fields?.length) return formData;

  const normalizedFormData = normalizeFormData(formData);
  const lockedByName = new Map(lockedContext.fields.map(field => [field.name, field]));
  const receivedFields = formFields(normalizedFormData);
  const receivedNames = new Set(receivedFields.map(field => field.name));
  const mergedFields = receivedFields.map(field => {
    const lockedField = lockedByName.get(field.name);

    if (!lockedField) return field;

    return { ...field, ...lockedField, disabled: true, readonly: true };
  });
  const missingLockedFields = lockedContext.fields
    .filter(field => !receivedNames.has(field.name))
    .map(field => ({ ...field, disabled: true, readonly: true }));
  const receivedScheme = formScheme(normalizedFormData);
  const receivedSchemeNames = new Set(receivedScheme.flat().map(item => item?.name));
  const missingLockedScheme = lockedContext.scheme
    .map(row => row.filter(item => !receivedSchemeNames.has(item?.name)))
    .filter(row => row.length > 0);

  return {
    ...normalizedFormData,
    context: { ...(normalizedFormData.context || {}), ...(lockedContext.context || {}) },
    components: {
      ...normalizedFormData.components,
      fields: [...mergedFields, ...missingLockedFields],
      scheme: [...receivedScheme, ...missingLockedScheme],
    },
  };
};

const prepareIssueFormSchema = (formData, values = {}) => {
  const normalizedFormData = normalizeFormData(formData);
  const contextParams = getIssueContextParams(normalizedFormData, values);
  const serviceId = values.service_id ?? fieldValue(normalizedFormData, values, "service_id");
  const fields = formFields(normalizedFormData).map(field => {
    const waitsForService = field.name === "work_category_id" && !hasValue(serviceId);
    const disabled = Boolean(field.disabled || field.readonly || waitsForService);
    const commonField = {
      ...field,
      readonly: disabled,
      disabled,
    };

    if (!ISSUE_FILTER_FIELD_NAMES.includes(field.name)) return commonField;

    const optionsParams = {
      ...(field.options_params || {}),
      ...contextParams,
    };

    if (field.name === "work_category_id" && hasValue(serviceId)) {
      optionsParams.service_id = serviceId;
    }

    return {
      ...commonField,
      refetch_on_change: ISSUE_REFETCH_FIELD_NAMES.includes(field.name),
      options_params: optionsParams,
      ...(field.name === "work_category_id" && !field.watch ? { watch: { parent: "service_id" } } : {}),
    };
  });

  return {
    ...normalizedFormData,
    components: {
      ...normalizedFormData.components,
      fields,
    },
  };
};

const getIssueRefetchFieldParams = (formData, values = {}) =>
  compactParams(
    formFields(formData).reduce((result, field) => {
      if (field.refetch_on_change) {
        result[field.name] = fieldValue(formData, values, field.name);
      }

      return result;
    }, {}),
  );

const buildIssueFormRefetchParams = ({ formData, formState = {}, changedField }) => {
  const values = Object.entries(formState).reduce((result, [name, state]) => {
    if (state !== undefined) result[name] = state?.field;

    return result;
  }, {});
  values[changedField.name] = changedField.value;

  return getIssueRefetchFieldParams(formData, values);
};

const buildIssueFormRequestParams = (formData, values = {}) =>
  compactParams({
    ...getIssueContextParams(formData, values),
    ...getIssueRefetchFieldParams(formData, values),
    work_category_id: values.work_category_id,
  });

const serializeIssueFile = file =>
  typeof File !== "undefined" && file instanceof File ? { name: file.name, file, type: file.type } : file;

const buildIssueSubmissionValues = (values = {}, formData = null) => {
  const submissionValues = { ...values };

  formFields(formData).forEach(field => {
    if (field.type !== "file" || !hasOwn(values, field.name)) return;

    const value = values[field.name];
    submissionValues[field.name] = Array.isArray(value) ? value.map(serializeIssueFile) : serializeIssueFile(value);
  });

  return submissionValues;
};

const shouldRefetchIssueForm = (name, field = {}) =>
  name !== "work_category_id" && (Boolean(field.refetch_on_change) || ISSUE_REFETCH_FIELD_NAMES.includes(name));

module.exports = {
  ISSUE_CONTEXT_FIELD_NAMES,
  ISSUE_REFETCH_FIELD_NAMES,
  buildIssueFormRefetchParams,
  buildIssueFormRequestParams,
  buildIssueSubmissionValues,
  fieldDependsOn,
  formFields,
  formScheme,
  getIssueContextParams,
  getLockedIssueContext,
  mergeLockedIssueContext,
  normalizeFormData,
  prepareIssueFormSchema,
  reconcileIssueFormValues,
  serializeIssueFieldValue,
  shouldRefetchIssueForm,
  valuesFromIssueFormSchema,
};
